import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';

/**
 * Minimal anchored popover — relative wrapper + absolutely positioned panel,
 * closes on outside click / Escape. Same pattern as ButtonMenu, but with a
 * caller-rendered trigger so pickers can style their own control.
 */
export const Popover: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  /** Panel classes (width etc). */
  panelClassName?: string;
  align?: 'left' | 'right';
  /** Open the panel above the trigger (for a menu in a dialog footer). */
  side?: 'bottom' | 'top';
  className?: string;
}> = ({ open, onOpenChange, trigger, children, panelClassName, align = 'left', side = 'bottom', className }) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div className={clsx('relative', className)} ref={wrapRef}>
      {trigger}
      {open && (
        <div
          className={clsx(
            'absolute z-40 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated',
            side === 'top' ? 'bottom-full mb-1.5' : 'mt-1.5',
            align === 'right' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  separatorAbove?: boolean;
}

/** Icon-trigger row-actions dropdown (⋮ menu). */
export const ActionMenu: React.FC<{
  items: ActionMenuItem[];
  trigger: React.ReactNode;
  align?: 'left' | 'right';
  side?: 'bottom' | 'top';
}> = ({ items, trigger, align = 'right', side = 'bottom' }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align={align}
      side={side}
      panelClassName="w-52 py-1"
      trigger={
        <span onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
          {trigger}
        </span>
      }
    >
      {items.map((it, i) => (
        <React.Fragment key={`${it.label}-${i}`}>
          {it.separatorAbove && <div className="my-1 border-t border-border" />}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); it.onSelect(); }}
            className={clsx(
              'flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] font-medium transition-colors',
              it.danger ? 'text-danger hover:bg-danger-soft' : 'text-text-primary hover:bg-surface-alt',
            )}
          >
            {it.icon}
            {it.label}
          </button>
        </React.Fragment>
      ))}
    </Popover>
  );
};
